import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Building, DollarSign, Search, Briefcase, Globe } from 'lucide-react';

const PublicJobBoard = () => {
  const { slug } = useParams();
  const [org, setOrg] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, [slug]);

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/orgs/${slug}/positions`
      );
      if (response.data.success) {
        setOrg(response.data.data.organization);
        setPositions(response.data.data.positions);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPositions = positions.filter(p =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.location || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Company not found</h1>
          <p className="text-gray-600">The company page you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-job-board">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-5 mb-4">
            {org.logo_url ? (
              <img src={org.logo_url} alt={org.name} className="w-16 h-16 rounded-lg object-cover" />
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Building className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <Link to={`/${slug}/about`} className="hover:text-blue-600 transition-colors">
                <h1 className="text-3xl font-bold text-gray-900" data-testid="org-name">{org.name}</h1>
              </Link>
              {org.description && (
                <p className="text-gray-600 mt-1 max-w-2xl">{org.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search + Count */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {filteredPositions.length} open position{filteredPositions.length !== 1 ? 's' : ''}
          </h2>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search positions..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              data-testid="job-search-input"
            />
          </div>
        </div>

        {/* Positions List */}
        {filteredPositions.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No positions found</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="positions-list">
            {filteredPositions.map((position) => (
              <Link
                key={position.id}
                to={`/${slug}/jobs/${position.id}`}
                className="block bg-white rounded-lg border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm transition-all"
                data-testid={`position-card-${position.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-600 hover:text-blue-700 mb-2">
                      {position.title}
                    </h3>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                      {position.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" /> {position.location}
                        </span>
                      )}
                      {position.employment_type && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" /> <span className="capitalize">{position.employment_type}</span>
                        </span>
                      )}
                      {position.workplace && (
                        <span className="flex items-center gap-1">
                          <Globe className="w-3.5 h-3.5" /> <span className="capitalize">{position.workplace}</span>
                        </span>
                      )}
                      {(position.salary_min || position.salary_max) && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3.5 h-3.5" />
                          {position.salary_currency || 'EUR'} {position.salary_min?.toLocaleString()}
                          {position.salary_max && ` - ${position.salary_max.toLocaleString()}`}
                        </span>
                      )}
                    </div>
                    {position.must_have_skills && position.must_have_skills.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {position.must_have_skills.slice(0, 5).map((skill, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                            {skill}
                          </span>
                        ))}
                        {position.must_have_skills.length > 5 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                            +{position.must_have_skills.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {position.experience_level && (
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded capitalize flex-shrink-0 ml-4">
                      {position.experience_level}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-white mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-sm text-gray-500">
          Powered by <span className="font-semibold text-gray-700 notranslate" translate="no">JobastiIQ</span>
        </div>
      </div>
    </div>
  );
};

export default PublicJobBoard;
